import React, { useState } from 'react';
import { ATTIC_STEPS, KIND_GROUPS, STEP_TYPES, newStep, savePlan, visitCount, withRebate } from '../../services/jobs';

/**
 * Set up (or, once approved, change) a job's plan: steps picked from the step list (visits, payments, office —
 * or "Other…" for a one-off visit), moved up / down or removed, plus whether it's an Enbridge rebate job (adds /
 * removes the two Enbridge visits). Visits are counted from the steps; who's coming is set per visit when it's
 * booked. Save draft keeps working on it; Submit sends it to an admin to approve.
 */
const PlanEditor = ({ job, approved, onDone, onCancel }) => {
  const start = (job.plan && job.plan.steps && job.plan.steps.length ? job.plan.steps : withRebate(ATTIC_STEPS, job.rebate)).map((s) => ({ ...s }));
  const [steps, setSteps] = useState(start);
  const [rebate, setRebate] = useState(start.some((s) => s.key === 'pre_audit' || s.key === 'post_audit'));
  const [pick, setPick] = useState('');
  const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= steps.length) return;
    const next = steps.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };
  // a new step goes before Before & after report / Final payment when they're still at the end, else last
  const add = () => {
    if (!pick || (pick === 'other' && !other.trim())) return;
    if (pick === 'pre_audit' || pick === 'post_audit') {
      setRebate(true);
      setSteps(withRebate(steps, true));
    } else {
      const step = newStep(steps, pick, other);
      const tail = ['report', 'rebate_paperwork', 'final_payment'];
      let at = steps.length;
      while (at > 0 && tail.includes(steps[at - 1].key) && !tail.includes(pick) && steps[at - 1].status !== 'done') at -= 1;
      const next = steps.slice();
      next.splice(at, 0, step);
      setSteps(next);
    }
    setPick('');
    setOther('');
  };
  const save = async (submit) => {
    setBusy(true);
    setError('');
    try {
      onDone(await savePlan(job.id, { steps, rebate }, submit));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  const visits = visitCount(steps);

  return (
    <div className="plan-editor">
      <ol className="job-steps">
        {steps.map((s, i) => (
          <li key={s.key} className={`job-step ${s.status || 'pending'}`}>
            <span className="job-step-dot" aria-hidden="true">{s.status === 'done' ? '✓' : i + 1}</span>
            <span>{s.label}</span>
            <span className="plan-step-tools">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${s.label} up`}>↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label={`Move ${s.label} down`}>↓</button>
              <button type="button" onClick={() => setSteps(steps.filter((x) => x.key !== s.key))} disabled={s.status === 'done' || steps.length === 1} aria-label={`Remove ${s.label}`}>✕</button>
            </span>
          </li>
        ))}
      </ol>

      <div className="plan-custom">
        <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Add a step">
          <option value="">+ Add a step…</option>
          {KIND_GROUPS.map(([kind, title]) => (
            <optgroup key={kind} label={title}>
              {STEP_TYPES.filter((t) => t.kind === kind).map((t) => (
                <option key={t.type} value={t.type}>{t.label}</option>
              ))}
            </optgroup>
          ))}
          <option value="other">Other visit…</option>
        </select>
        <button type="button" onClick={add} disabled={!pick || (pick === 'other' && !other.trim())}>Add</button>
      </div>
      {pick === 'other' && (
        <div className="plan-custom">
          <input value={other} onChange={(e) => setOther(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Name of the visit, e.g. Electrician visit" maxLength={60} autoFocus />
        </div>
      )}

      <div className="plan-fields">
        <label className="plan-check">
          <input type="checkbox" checked={rebate} onChange={(e) => { setRebate(e.target.checked); setSteps(withRebate(steps, e.target.checked)); }} />
          Enbridge rebate job (pre- and post-inspection)
        </label>
        <p className="ca-muted">
          {visits} visit{visits === 1 ? '' : 's'} to the house. Dates and who’s coming are added on each step once the plan is approved.
        </p>
      </div>

      {error && <p className="plan-error">⚠ {error}</p>}
      <div className="plan-actions">
        <button type="button" className="status-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
        {approved ? (
          <button type="button" className="status-save" onClick={() => save(false)} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        ) : (
          <>
            <button type="button" className="status-cancel" onClick={() => save(false)} disabled={busy}>Save draft</button>
            <button type="button" className="status-save" onClick={() => save(true)} disabled={busy}>{busy ? 'Sending…' : 'Submit for approval'}</button>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanEditor;
